import { Container, Row, Col, Form, Card } from 'react-bootstrap';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sparkle from '../../../../components/Sparkle/Sparkle';
import { useNavigate } from 'react-router-dom';
import { RiSparklingFill } from '@remixicon/react';
import AccessModal from '../../../../components/Modal/AccessModal';
import SpinnerButton from '../../../../components/Button/SpinnerButton';
import { fakeResponse } from '../../../../utils/helpers.js';
import { authFetch } from '../../../../utils/apiClient.js';
import { mockCGV } from '../../../../utils/mockData.js';
import useAccessType from '../../../../hooks/useAccessType.js';
import DemoModal from '../../../../components/Modal/DemoModal.jsx';

function CGVFormScreen() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        websiteLink: '',
        companyName: '',
        country: '',
        email: '',
        currency: 'EUR',
        withdrawalPeriod: '14',
        paymentMethods: '',
        language: '',
    });
    const [validated, setValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [modalType, setModalType] = useState(null);
    const { getAccessType, isDemo } = useAccessType(true);
    const [showModal, setShowModal] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDemo = async () => {
        setIsLoading(true);
        const data = await generateFakeCGV();
        navigate('/terms/cgv/generated', { state: { cgv: data } });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }

        setValidated(false);

        if (isDemo) {
            setShowModal(true);
            return;
        }
        const accessType = getAccessType();
        if (accessType) {
            setModalType(accessType);
            setShowModal(true);
            return;
        }

        setIsLoading(true);
        const data = await generateCGV(formData);
        navigate('/terms/cgv/generated', { state: { cgv: data } });
    };

    async function generateCGV(cgvData) {
        try {
            const res = await authFetch("/api/terms/generate-cgv", {
                method: "POST",
                body: JSON.stringify(cgvData),
            });

            return await res.json();
        } catch (error) {
            console.error("Error generating CGV:", error);
            return null;
        }
    }

    async function generateFakeCGV() {
        return fakeResponse(mockCGV)
    }

    return (
        <>
            {isLoading ? (
                <Sparkle />
            ) : (
                <Container className="py-5">
                    <Row className="mb-5">
                        <Col xs={12} className="text-center">
                            <h2 className="title">{t('cgv.terms')}</h2>
                        </Col>
                    </Row>

                    <Card>
                        <Card.Body>
                            <Form noValidate validated={validated} onSubmit={handleSubmit} id="cgvForm">
                                {/* Informations de l'entreprise */}
                                <Row className="mb-4 g-4">
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="companyName">
                                            <Form.Label>{t('cgv.companyName')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('cgv.companyNameRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="websiteLink">
                                            <Form.Label>{t('cgv.websiteLink', 'Lien du site')} *</Form.Label>
                                            <Form.Control
                                                type="url"
                                                name="websiteLink"
                                                value={formData.websiteLink}
                                                onChange={handleChange}
                                                placeholder="https://..."
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('cgv.urlInvalid', 'URL invalide')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-4 g-4">
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="country">
                                            <Form.Label>{t('cgv.country')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('cgv.countryRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="language">
                                            <Form.Label>{t('app.language')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="language"
                                                value={formData.language}
                                                onChange={handleChange}
                                                placeholder="Français, English, Deutsch..."
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('cgv.languageRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-4 g-4">
                                    <Col xs={12}>
                                        <Form.Group controlId="email">
                                            <Form.Label>{t('cgv.email')} *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('cgv.emailRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Informations commerciales */}
                                <Row className="mb-4 g-4">
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="currency">
                                            <Form.Label>{t('cgv.currency')} *</Form.Label>
                                            <Form.Select
                                                name="currency"
                                                value={formData.currency}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="EUR">EUR (€)</option>
                                                <option value="USD">USD ($)</option>
                                                <option value="GBP">GBP (£)</option>
                                                <option value="CHF">CHF (CHF)</option>
                                                <option value="CAD">CAD ($)</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="paymentMethods">
                                            <Form.Label>{t('cgv.paymentMethods')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="paymentMethods"
                                                value={formData.paymentMethods}
                                                onChange={handleChange}
                                                placeholder={t('cgv.paymentMethodsType')}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('cgv.paymentMethodsRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Rétractation et retours */}
                                <Row className="mb-4 g-4">
                                    <Col xs={12}>
                                        <Form.Group controlId="withdrawalPeriod">
                                            <Form.Label>{t('cgv.withdrawalPeriod')} *</Form.Label>
                                            <Form.Select
                                                name="withdrawalPeriod"
                                                value={formData.withdrawalPeriod}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="14">14 {t('app.days')}</option>
                                                <option value="30">30 {t('app.days')}</option>
                                                <option value="60">60 {t('app.days')}</option>
                                                <option value="90">90 {t('app.days')}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} md={{ span: 6, offset: 6 }}>
                                        <SpinnerButton
                                            type="submit"
                                            className="w-100 bg-pink"
                                            disabled={isLoading}
                                            label={<>
                                                <span className="me-2">{t('app.generate')}</span>
                                                <RiSparklingFill size={20} />
                                            </>}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>

                    <AccessModal
                        show={showModal && !isDemo}
                        setShow={setShowModal}
                        modalType={modalType}
                    />

                    <DemoModal
                        show={showModal && isDemo}
                        setShow={setShowModal}
                        onAction={handleDemo}
                    />
                </Container>
            )}
        </>
    );
}

export default CGVFormScreen;
